import React, { useRef } from 'react';

// Hooks
import { useFetchCalendarMeetings } from 'Shared/utils/hooks/use-fetch-calendar-meetings';
import { useFetchHomePageData } from 'Shared/utils/hooks/use-fetch-home-page-data';
import { useMembers } from 'Shared/utils/hooks';
import { useTranslation } from 'Shared/utils/hooks/use-translation';
import useElementDimensions from 'Wpds/hooks/useElementDimensions';

// Types
import { ItemType } from 'Shared/types/MeetingItem.type';

// Utils
import { getCurrentMoment, formatDate } from 'Shared/utils/DateTime.utils';

// Components
import DayEvent from './_partials/DayEvent';
import EmptyStateContainer from 'Shared/components/EmptyStateContainer/EmptyStateContainer';
import { WpButtonGroup } from 'Wpds/components/button';
import OAuthButton from '../OAuthButton/OAuthButton';
import WpButton from 'Wpds/components/button/WpButton';
import WpIcon from 'Wpds/components/icon/WpIcon';

// Styles
import './DayCalendarMeetings.scss';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';

const DayCalendarMeetings = () => {
  const { tr } = useTranslation();
  useMembers();
  const containerRef = useRef();

  const { hasMeetings, isCalendarConnected, resyncInProgress, handleResyncCalendar, isLoading, calendarMeetings } =
    useFetchCalendarMeetings();
  const { isLoading: isFetchingDiscussionTopics, response: discusstionTopicsResponse } = useFetchHomePageData({
    itemType: ItemType.discussionTopic,
  });

  const date = formatDate(getCurrentMoment(), 'MMMM Do');

  const { height } = useElementDimensions({ elementRef: containerRef });

  const eventsWithDt = calendarMeetings.map(meeting => {
    return {
      ...meeting,
      discussionTopics:
        discusstionTopicsResponse?.data?.data.filter(dt => dt.attributes.workspaceId === meeting.workspaceId) || [],
    };
  });

  const baseClassName = 'DayCalendarMeetings';
  const showLoadingSpinner = (!hasMeetings && (isLoading || resyncInProgress)) || isFetchingDiscussionTopics || !height;
  const showLoadingSpinnerOnResyncButton = isLoading || resyncInProgress;
  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__title-wrapper`}>
        <h3 className={`${baseClassName}__title`}>{tr('perform.calendar_meetings.day_calendar.title')}</h3>
        {!showLoadingSpinner && (
          <WpTooltip placement="top">
            <WpTooltipTrigger>
              <WpButton variant="tertiary" onClick={handleResyncCalendar} loading={showLoadingSpinnerOnResyncButton}>
                <WpIcon name="sync" />
              </WpButton>
            </WpTooltipTrigger>
            <WpTooltipContent>{tr('perform.calendar_meetings.day_calendar.resync_tooltip')}</WpTooltipContent>
          </WpTooltip>
        )}
      </div>
      {isCalendarConnected && (
        <>
          <p className={`${baseClassName}__date`}>{date}</p>
          <div className={`${baseClassName}__calendar`} ref={containerRef}>
            {!showLoadingSpinner && (
              <>
                {eventsWithDt.map(event => (
                  <DayEvent event={event} key={event.id} />
                ))}
              </>
            )}
            {showLoadingSpinner && <>{tr('perform.calendar_meetings.day_calendar.loading')}</>}
            {!showLoadingSpinner && !hasMeetings && (
              <div className={`${baseClassName}__empty`}>
                <EmptyStateContainer title={tr('perform.calendar_meetings.day_calendar.no_meetings')} titleTag="h5" />
              </div>
            )}
          </div>
        </>
      )}
      {!isCalendarConnected && (
        <div className={`${baseClassName}__empty`}>
          <EmptyStateContainer
            title={tr('perform.calendar_meetings.connect.title')}
            titleTag="h5"
            inContainer
            subTitle={tr('perform.calendar_meetings.connect.description')}
            button={
              <WpButtonGroup stacked>
                <OAuthButton
                  oauthProvider="google"
                  text={tr('perform.calendar_meetings.connect.google_button')}
                  withCalendar
                  block
                  color="gray"
                  variant="primary"
                />
                <OAuthButton
                  oauthProvider="office365"
                  text={tr('perform.calendar_meetings.connect.office365_button')}
                  withCalendar
                  block
                  color="gray"
                  variant="primary"
                />
              </WpButtonGroup>
            }
          />
        </div>
      )}
    </div>
  );
};

export default DayCalendarMeetings;
